import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { SvgSpacesMediumDs, SvgSpacesMediumExternalServer, SvgSpacesMediumFile, SvgSpacesMediumFlow, SvgSpacesMediumTable, SvgVirtualConnection, SvgVirtualConnectionTable } from '../../../../../gen-src/icons';
import SpacesDataStatus from '../../../../../gen-src/icons/SpacesDataStatus';
import SpacesDataStatusActive from '../../../../../gen-src/icons/SpacesDataStatusActive';
import WarningHighSeverity from '../../../../../gen-src/icons/WarningHighSeverity';
import WarningHighSeverityActive from '../../../../../gen-src/icons/WarningHighSeverityActive';
import { RemoteType } from '../../../../../gen-src/queries';
import { TableauUser, UserLinkDisplayComponent, UserLinkProps, UserTextDisplayComponent } from '../UserDisplayComponent';
import { SingleLineLinkWithIcon, SingleLineTextWithIcon } from '../ViewerPaneLayoutComponents';
import { RemoteTypeIcon } from '../dataGrid/cellFormatters/RemoteTypeIcon';
import { DataQualityType } from '../dataQualityWarnings/DataQualityType';
import { IndicatorDetailAssetStyle, IndicatorDetailAuthorStyle, IndicatorDetailDateStyle, IndicatorDetailMessageStyle, IndicatorDetailPermissionRequired, IndicatorStatusChipIconClass } from '../style/DataQualityIndicatorStyles';
import { FormattedMarkupWrapperInTooltip } from '../style/MarkupStyles';
import { DataQualityDetailPanelWidth } from '../style/StyleConstants';
import { linkClass } from '../style/VizPortalStyles';
import { interpolateString } from '../utils/StringInterpolation';
import { assertUnreachable } from '../utils/TypescriptHelpers';

/**
 * @deprecated TFS-1478812
*/
export type RenderIndicatorProps = {
  readonly kind: DataQualityIndicatorKind;
  readonly author: TableauUser;
  readonly localizedDate: string;
  readonly type: DataQualityType;
  readonly message: string | null;
  readonly newTab?: boolean; // TODO: maybe not used
  readonly linksDisabled?: boolean; // TOOD: maybe not used
  readonly id: string;
};

/**
 * @deprecated TFS-1478812
*/
export type DataQualityIndicatorAssetType = 'CloudFile'
  | 'DatabaseServer'
  | 'DatabaseTable'
  | 'File'
  | 'Flow'
  | 'PublishedDatasource'
  | 'WebDataConnector'
  | 'VirtualConnectionTable'
  | 'VirtualConnection'
  | 'Column'
  | null;

/**
 * @deprecated TFS-1478812
*/
export type IndicatorSubjectArea =
  DataQualityIndicatorAssetType
  | 'View'
  | 'Metric'
  | 'Workbook'
  | 'EmbeddedDatasource'
  | 'Field'
  | null;

/**
 * @deprecated TFS-1478812
*/
export type RenderIndicatorAssetProps = {
  readonly subjectArea: IndicatorSubjectArea;
  readonly assetName: string | null;
  readonly assetType: DataQualityIndicatorAssetType | null;
  readonly assetUrl: string | null;
  readonly indicators: RenderIndicatorProps[];
  readonly remoteType?: RemoteType;
  readonly linksDisabled?: boolean;
};

/**
 * @deprecated TFS-1478812
*/
export type RenderIndicatorsProps = {
  readonly newTab?: boolean;
  readonly linksDisabled?: boolean;
  readonly childrenIndicatorAndAsset: ReadonlyArray<RenderIndicatorAssetProps>;
  readonly upstream: ReadonlyArray<RenderIndicatorAssetProps>;
} & RenderIndicatorAssetProps;

/**
 * @deprecated TFS-1478812
*/
export type DataQualityCertificationKind = 'certification';
/**
 * @deprecated TFS-1478812
*/
export type DataQualityWarningKind = 'standard-warning' | 'severe-warning';
/**
 * @deprecated TFS-1478812
*/
export type DataQualityIndicatorKind = DataQualityCertificationKind | DataQualityWarningKind;

/**
 * @deprecated TFS-1478812
*/
export const DataQualityDetailChipId = 'data-quality-detail-chip-id';
/**
 * @deprecated TFS-1478812
*/
export const DataQualityDetailMessageId = 'data-quality-detail-message-id';


function assetIconForType(typename: DataQualityIndicatorAssetType, remoteType?: RemoteType): JSX.Element | null {
  switch (typename) {
    case null:
      return null;
    case 'CloudFile':
    case 'File':
      return <SvgSpacesMediumFile />;
    case 'DatabaseServer':
    case 'WebDataConnector':
      return <SvgSpacesMediumExternalServer />;
    case 'DatabaseTable':
      return <SvgSpacesMediumTable />;
    case 'Flow':
      return <SvgSpacesMediumFlow />;
    case 'PublishedDatasource':
      return <SvgSpacesMediumDs />;
    case 'Column':
      return remoteType ? <RemoteTypeIcon remoteType={remoteType} /> : null;
    case 'VirtualConnection':
      return <SvgVirtualConnection />;
    case 'VirtualConnectionTable':
      return <SvgVirtualConnectionTable />;
    default:
      assertUnreachable(typename);
      return null;
  }
}

function getPermissionRequiredMessage(): JSX.Element {
  const name = Messages.PermissionRequired();
  return <span key={'asset-' + name} style={IndicatorDetailPermissionRequired}>{name}</span>;
}

/**
 * @deprecated TFS-1478812
*/
export type InterpolateAssetProps = {
  assetName: string | null;
  assetType: DataQualityIndicatorAssetType | null;
  remoteType?: RemoteType;
  assetUrl: string | null;
  children?: JSX.Element;
};

/**
 * @deprecated TFS-1478812
*/
export function DataQualityDetailAsset(props: InterpolateAssetProps): JSX.Element | null {
  if (props.assetName === null) { return getPermissionRequiredMessage(); }
  const iconComponent = assetIconForType(props.assetType, props.remoteType);

  return (
    <IndicatorDetailAssetStyle>
      {
        props.assetUrl ?
          <SingleLineLinkWithIcon
            linkText={props.assetName || null}
            linkHref={props.assetUrl}
            iconComponent={iconComponent}
            iconSize={'small'}
          /> :
          <SingleLineTextWithIcon
            text={props.assetName || null}
            iconComponent={iconComponent}
            iconSize={'small'}
          />
      }
    </IndicatorDetailAssetStyle>
  )
}

function DataQualityAuthorLink(props: UserLinkProps) {
  return <a href={props.url} target='_blank' className={linkClass} title={props.name} rel="noreferrer">{props.name}</a>;
}

/**
 * @deprecated TFS-1478812
*/
export type DataQualityDetailAuthorElementProps = {
  readonly kind: DataQualityIndicatorKind;
  readonly linksDisabled: boolean;
  readonly author: TableauUser;
};

/**
 * @deprecated TFS-1478812
*/
export function DataQualityDetailAuthorElement(props: DataQualityDetailAuthorElementProps): JSX.Element {
  return (
    <IndicatorDetailAuthorStyle>
      <DataQualityDetailAuthorElementInner {...props} />
    </IndicatorDetailAuthorStyle>
  )
}

/**
 * @deprecated TFS-1478812
*/
export function DataQualityDetailAuthorElementInner(props: {
  readonly kind: DataQualityIndicatorKind;
  readonly linksDisabled: boolean;
  readonly author: TableauUser;
}): JSX.Element {
  const userElement = props.linksDisabled
    ? <UserTextDisplayComponent user={props.author} />
    : <UserLinkDisplayComponent user={props.author} linkComponent={DataQualityAuthorLink} />;
  const name = '[AUTHOR_NAME_TOKEN]';
  const span = <span key='author' data-tb-test-id={'Data-Quality-Detail-Author'}>{userElement}</span>;

  switch (props.kind) {
    case 'severe-warning':
    case 'standard-warning':
      return (
        <>
          {
            interpolateString(Messages.DataQualityAuthor({ name }), { 'AUTHOR_NAME_TOKEN': span })
          }
        </>
      );
    case 'certification':
      return (
        <>
          {
            interpolateString(Messages.DataQualityCertificationAuthor({ name }), { 'AUTHOR_NAME_TOKEN': span })
          }
        </>
      );
    default:
      assertUnreachable(props.kind);
      return <></>;
  }
}

/**
 * @deprecated TFS-1478812
*/
export function DataQualityDetailDateElement(props: {
  readonly localizedDate: string;
}): JSX.Element {
  return (
    <IndicatorDetailDateStyle>
      {props.localizedDate}
    </IndicatorDetailDateStyle>
  )
}

function MarkupOrSplitString(props: {
  readonly message: string | null;
  readonly linksDisabled?: boolean;
}): JSX.Element {

  const { message, linksDisabled } = props;
  const messageOrBlank = message || '';

  return (
    <FormattedMarkupWrapperInTooltip markup={messageOrBlank} linksDisabled={linksDisabled} />
  );
}

/**
 * @deprecated TFS-1478812
*/
export function DataQualityDetailMessageElement(props: {
  readonly assetName: string | null;
  readonly message: string | null;
  readonly linksDisabled?: boolean;
}): JSX.Element {
  if (props.assetName === null) {
    return (
      <IndicatorDetailMessageStyle id={DataQualityDetailMessageId}>
        {Messages.PermissionRequiredMessage()}
      </IndicatorDetailMessageStyle>
    );
  }

  return (
    <IndicatorDetailMessageStyle>
      <MarkupOrSplitString {...props} />
    </IndicatorDetailMessageStyle>
  );
}

/**
 * @deprecated TFS-1478812
*/
export type DataQualityDetailPanelSide = 'left' | 'right';

/**
 * @deprecated TFS-1478812
*/
export function getTooltipAnchorClientRect(el?: HTMLElement): ClientRect {
  if (el) {
    const clientRect = el.getBoundingClientRect();
    return {
      left: clientRect.left - DataQualityDetailPanelWidth,
      right: clientRect.right,
      top: clientRect.top,
      bottom: clientRect.bottom,
    } as ClientRect;
  }

  return {} as ClientRect;
}

function DataStatusAnchored(props: { hover: boolean, isSevere: boolean }): JSX.Element {
  if (props.hover) {
    return props.isSevere ?
      <WarningHighSeverityActive className={IndicatorStatusChipIconClass} /> :
      <SpacesDataStatusActive className={IndicatorStatusChipIconClass} />;
  } else {
    return props.isSevere ?
      <WarningHighSeverity className={IndicatorStatusChipIconClass} /> :
      <SpacesDataStatus className={IndicatorStatusChipIconClass} />;
  }
}

/**
 * @deprecated TFS-1478812
*/
export function DataStatusIcon(props: { hover: boolean, isSevere: boolean }): JSX.Element {
  return (
    <DataStatusAnchored hover={props.hover} isSevere={props.isSevere} />
  );
}
